/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefCallback } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import { curry } from 'ramda';

/**
 * Curried function that returns a string containing the value of a specified CSS property.
 * @param el The Element for which to get the computed style.
 * @param prop A string representing the property name (in hyphen case) to be checked.
 * @returns A string containing the value of the property. If not set, returns the empty string.
 */
const getCssPropValue = curry((el: Element, prop: string) =>
  getComputedStyle(el).getPropertyValue(prop)
);

/**
 * Hook for reading CSS property values.
 * @returns A tuple with a reference that can be attached to a DOM element and a function that
 * returns a string containing a specified CSS property.
 */
const usePropertyValue = () => {
  const initValue = (_: string) => '';
  const [getPropertyValue, setPropertyValue] = useState<(s: string) => string>(
    () => (_: string) => ''
  );

  const setRef = useCallback((node: null | HTMLElement) => {
    setPropertyValue(node !== null ? () => getCssPropValue(node) : () => initValue);
  }, []) as RefCallback<HTMLElement>;

  return [setRef, getPropertyValue] as [
    RefCallback<HTMLElement>,
    CSSStyleDeclaration['getPropertyValue']
  ];
};

export { usePropertyValue };
